'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Breadcrumbs from '@/components/ui/breadcrumbs';
import { motion } from 'framer-motion';
import { getPackageBySlug } from '@/data/b2b';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const garmentTypes = [
  'T-Shirts',
  'Polo Shirts',
  'Hoodies',
  'Sweatshirts',
  'Jackets',
  'Workwear',
  'Staff Uniform / Workwear Package',
  'Clothing Brand / Bulk Print Run',
  'School Uniforms',
  'Caps/Hats',
  'Banners',
  'Live Embroidery / Retail Activation',
  'Other',
];

const printMethods = [
  'DTG (Direct-to-Garment)',
  'DTF (Direct-to-Film)',
  'Embroidery',
  'Banner Printing',
  'Not Sure — Advise Me',
];

const quantityRanges = [
  '1–10',
  '11–25',
  '26–50',
  '51–100',
  '100+',
];

export default function QuotePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    garmentType: '',
    printMethod: '',
    quantity: '',
    deadline: '',
    details: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill from ?pack=<slug> ("Quote this pack" buttons) or ?type=<option>
  // (sector and brand-page CTAs). window.location keeps this client page free
  // of the useSearchParams suspense requirement.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pack = getPackageBySlug(params.get('pack') || '');
    const type = params.get('type');
    // One-time init from the URL after hydration — a mount effect is the safe
    // place to read window.location without desyncing the server-rendered form.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormData((prev) => {
      if (pack) {
        return {
          ...prev,
          garmentType: pack.quoteGarmentType,
          details:
            prev.details ||
            `Quote request: ${pack.name} (guide price ${pack.guidePrice}). Includes: ${pack.includes.join(', ')}. Sizes and any changes: `,
        };
      }
      if (type && garmentTypes.includes(type)) {
        return { ...prev, garmentType: type };
      }
      return prev;
    });
  }, []);
  const [error, setError] = useState(false);

  const GAS_URL = 'https://script.google.com/macros/s/AKfycbwKQvAtPNjwpJmql7FXKu8bZ4ZHo_W4HnO6EjRVBvYiwZcrlUPkcONAwyOAkZPQv3xv/exec';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);

    try {
      // Send via hidden iframe (fire-and-forget, same pattern as Lapland Famille)
      const iframe = document.createElement('iframe');
      iframe.name = 'gas_notify_' + Date.now();
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const hiddenForm = document.createElement('form');
      hiddenForm.method = 'POST';
      hiddenForm.action = GAS_URL;
      hiddenForm.target = iframe.name;

      const fields: Record<string, string> = {
        form_type: 'quote',
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        garmentType: formData.garmentType,
        printMethod: formData.printMethod,
        quantity: formData.quantity,
        deadline: formData.deadline,
        details: formData.details,
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        hiddenForm.appendChild(input);
      });

      document.body.appendChild(hiddenForm);
      hiddenForm.submit();
      hiddenForm.remove();
      setTimeout(() => iframe.remove(), 10000);

      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Breadcrumbs items={[{ label: 'Get a Quote' }]} />
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[48rem] mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
              No Obligation, No Minimum Order
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Get a Quote
            </h1>
            <p className="text-lg lg:text-xl text-gray-300 leading-relaxed">
              Tell us what you need and we&apos;ll get back to you with a price within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="max-w-[42rem] mx-auto">
            {submitted ? (
              <motion.div
                className="p-12 bg-[var(--color-off-white)] border border-[var(--color-border)] text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-green-50 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-10 h-10 text-green-600"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-3">Quote Request Received</h2>
                <p className="text-[var(--color-text-secondary)] mb-6">
                  Thank you! We&apos;ll review your requirements and send you a quote within 24 hours.
                </p>
                <Link
                  href="/"
                  className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold bg-brand-blue text-white hover:bg-brand-blue-hover transition-colors uppercase tracking-wider"
                >
                  Back to Home
                </Link>
              </motion.div>
            ) : (
              <motion.form onSubmit={handleSubmit} className="space-y-8" {...fadeUp}>
                {/* Contact Details */}
                <div>
                  <h2 className="text-xl font-bold mb-6">Your Details</h2>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div>
                  <h2 className="text-xl font-bold mb-6">Project Details</h2>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="garmentType" className="block text-sm font-medium mb-2">
                          What do you need? *
                        </label>
                        <select
                          id="garmentType"
                          required
                          value={formData.garmentType}
                          onChange={(e) => setFormData({ ...formData, garmentType: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        >
                          <option value="">Select type...</option>
                          {garmentTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="printMethod" className="block text-sm font-medium mb-2">
                          Print method
                        </label>
                        <select
                          id="printMethod"
                          value={formData.printMethod}
                          onChange={(e) => setFormData({ ...formData, printMethod: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        >
                          <option value="">Select method...</option>
                          {printMethods.map((method) => (
                            <option key={method} value={method}>
                              {method}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="quantity" className="block text-sm font-medium mb-2">
                          Quantity *
                        </label>
                        <select
                          id="quantity"
                          required
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        >
                          <option value="">Select quantity...</option>
                          {quantityRanges.map((range) => (
                            <option key={range} value={range}>
                              {range}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="deadline" className="block text-sm font-medium mb-2">
                          When do you need it by?
                        </label>
                        <input
                          type="text"
                          id="deadline"
                          placeholder="e.g. 2 weeks, end of April"
                          value={formData.deadline}
                          onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                          className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="details" className="block text-sm font-medium mb-2">
                        Tell us more about your project
                      </label>
                      <textarea
                        id="details"
                        rows={5}
                        placeholder="Describe your design, colours, sizes, or anything else we should know..."
                        value={formData.details}
                        onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                        className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors resize-vertical"
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-600">
                    Something went wrong. Please try again or email us directly at hello@oktshirt.co.uk.
                  </p>
                )}

                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center px-10 py-4 text-sm font-semibold bg-brand-blue text-white hover:bg-brand-blue-hover transition-colors uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Request Quote'}
                  </button>
                  <p className="text-xs text-[var(--color-text-muted)] pt-2">
                    No obligation. We typically respond within 24 hours.
                  </p>
                </div>
              </motion.form>
            )}
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <div className="max-w-[48rem] mx-auto text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Prefer to Talk?
            </h2>
            <p className="text-[var(--color-text-secondary)] mb-8">
              Call us or send a WhatsApp message for an instant quote on simple orders.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:02045208521"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-semibold bg-brand-blue text-white hover:bg-brand-blue-hover transition-colors uppercase tracking-wider"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                  />
                </svg>
                020 4520 8521
              </a>
              <a
                href="https://wa.me/447403343433"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-semibold border border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white transition-colors uppercase tracking-wider"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
